import { useCallback, useEffect, useMemo, useState } from 'react';

import { useParams } from '@tanstack/react-router';
import classNames from 'classnames';
import { groupBy, keyBy } from 'lodash-es';

import { isString } from '@iconik-io/core/src/is_string';
import { OBJECT_TYPE_ASSETS } from '@iconik-io/iconik-api/src/types/object_types';
import type { PrefixTreeNode } from '@iconik-io/shared-lib/src/react/components/panels/transcriptions_panel/types';
import { usePrevious } from '@iconik-io/shared-lib/src/react/hooks/use_previous';
import { selectAssetVersionsExtendedMap } from '@iconik-io/shared-lib/src/redux/entities/selectors/versions';
import { getTranscriptionProperties } from '@iconik-io/shared-lib/src/redux/files/thunks';
import { languages } from '@iconik-io/shared-lib/src/utils/constants/files';
import { Icon, Button as UADButton } from '@iconik-io/uad';

import { isContextMenuEmpty } from '../../components/context_menu/utils';
import ModalsManager from '../../components/modals/modals_manager/modals_manager';
import { AMAZON_TRANSCRIBE_LANGUAGES } from '../../components/modals/transcribe_confirm/amazon_languages';
import { GOOGLE_TRANSCRIBE_LANGUAGES } from '../../components/modals/transcribe_confirm/google_languages';
import { REV_TRANSCRIBE_LANGUAGES } from '../../components/modals/transcribe_confirm/rev_languages';
import { TranscriptionsConfirmChangeSegmentsSpeakerModal } from '../../components/modals/transcriptions_change_segments_speaker/transcriptions_change_segments_speaker';
import TranscriptionsConfirmChangeWordsSpeaker from '../../components/modals/transcriptions_change_words_speaker/transcriptions_change_words_speaker';
import { TranscriptionSpeakersModal } from '../../components/modals/transcriptions_edit_speakers/transcriptions_edit_speakers';
import TranscriptionSubtitlesModal from '../../components/modals/transcriptions_edit_subtitles/transcriptions_edit_subtitles';
import NoContentPanel from '../../components/no_content_panel/no_content_panel';
import TooltipTrigger from '../../components/tooltip_trigger/tooltip_trigger';
import TriggerContextMenu from '../../components/trigger_context_menu/trigger_context_menu';
import { TRANSCRIPTIONS } from '../../constants/help_urls';
import {
  TRANSCRIBE_CONFIRM_MODAL,
  TRANSCRIPTIONS_CHANGE_SEGMENTS_SPEAKER,
  TRANSCRIPTIONS_CHANGE_WORDS_SPEAKER,
  TRANSCRIPTIONS_EDIT_SPEAKERS,
  TRANSCRIPTIONS_EDIT_SUBTITLES,
} from '../../constants/modals';
import VARIANTS from '../../constants/variants';
import {
  getSelectedTranscriptionPropertiesIdByAssetId,
  selectTranscriptionPropertiesByAssetId,
  selectTranscriptionSegmentsByAssetId,
} from '../../ducks/asset_transcriptions/selectors';
import {
  setSelectedTranscriptionPropertiesId,
  setSpeakerMode,
} from '../../ducks/asset_transcriptions/slice';
import { getTranscriptionSegments } from '../../ducks/asset_transcriptions/thunks';
import { openModal } from '../../ducks/modals_manager/slice';
import { selectAssetProxy } from '../../ducks/pages/asset/selectors';
import { selectCurrentVersionId } from '../../ducks/version/selectors';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import Button from '../../ui/button/button';
import LoadingBar from '../../ui/loading_bar/loading_bar';
import SearchInput from '../../ui/search_input/search_input';
import SelectField from '../../ui/select_field/select_field';
import { selectUserRoles } from '../../utils/get_user_roles';
import { addObjectType } from '../../utils/object_type';
import AssetTranscriptionsGearboxContextMenu, {
  getDefinition,
} from './gearbox_context_menu';
import { TranscriptionsSegmentsMode } from './segments_mode/segments_mode';
import {
  selectAllWords,
  selectTranscriptionPartsAndSpeakerLabels,
  selectWordsIndexTree,
} from './selectors';
import TranscriptionsSpeakerMode from './speaker_mode/speaker_mode';

import styles from './asset_transcriptions.module.css';

function MenuTriggerButton() {
  return (
    <Button
      wideBasis={false}
      noBorder
      variant={VARIANTS.WHITE}
      iconName="menu_context"
    />
  );
}

export default function AssetTranscriptions() {
  const dispatch = useAppDispatch();

  const [searchQuery, setSearchQuery] = useState('');
  const [searchResultIds, setSearchResultIds] = useState<string[]>([]);
  const [searchResultSegmentsIds, setSearchResultSegmentsIds] = useState<
    string[]
  >([]);
  const [currentMatchIndex, setCurrentMatchIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);

  const { id: assetId, versionId } = useParams({ strict: false });
  const asset = useAppSelector((state) =>
    assetId ? state.entities.assets[assetId] : undefined,
  );
  const currentVersionId = useAppSelector((state) =>
    selectCurrentVersionId(state, assetId, versionId),
  );
  const userRoles = useAppSelector(selectUserRoles);
  const tree = useAppSelector((state) => selectWordsIndexTree(state, assetId));
  const { allWordsIndex } = useAppSelector((state) =>
    selectAllWords(state, assetId),
  );
  const transcriptionProperties = useAppSelector((state) =>
    selectTranscriptionPropertiesByAssetId(
      state,
      assetId ?? '',
      currentVersionId,
    ),
  );
  const selectedTranscriptionPropertiesId = useAppSelector((state) =>
    getSelectedTranscriptionPropertiesIdByAssetId(state, assetId ?? ''),
  );
  const speakerMode = useAppSelector(
    (state) => state.assetTranscriptions.speakerMode,
  );
  const assetProxy = useAppSelector((state) =>
    selectAssetProxy(state, assetId, versionId),
  );
  const { speakerLabels } = useAppSelector((state) =>
    selectTranscriptionPartsAndSpeakerLabels(state, assetId),
  );
  const transcriptions = useAppSelector((state) =>
    selectTranscriptionSegmentsByAssetId(state, assetId ?? ''),
  );
  const transcriptionsMap = useMemo(() => {
    return keyBy(transcriptions, 'id');
  }, [transcriptions]);

  const searchResultTranscriptions = useMemo(() => {
    if (searchResultSegmentsIds.length === 0) {
      return [];
    }
    return searchResultSegmentsIds.map(
      (segmentId) => transcriptionsMap[segmentId],
    );
  }, [searchResultSegmentsIds, transcriptionsMap]);

  const orderedSearchResultIds = useMemo(
    () =>
      [...searchResultIds].sort(
        (a, b) => (allWordsIndex[a] ?? 0) - (allWordsIndex[b] ?? 0),
      ),
    [searchResultIds, allWordsIndex],
  );
  const totalMatches = orderedSearchResultIds.length;
  const currentMatchWordId = orderedSearchResultIds[currentMatchIndex];

  const handlePrevMatch = (): void => {
    if (totalMatches === 0) {
      return;
    }
    setCurrentMatchIndex((index) => (index - 1 + totalMatches) % totalMatches);
  };

  const handleNextMatch = (): void => {
    if (totalMatches === 0) {
      return;
    }
    setCurrentMatchIndex((index) => (index + 1) % totalMatches);
  };

  const transcribeStatus = useAppSelector((state) => {
    const assetVersionsMap = selectAssetVersionsExtendedMap(state, assetId);
    if (currentVersionId && assetVersionsMap[currentVersionId]) {
      return assetVersionsMap[currentVersionId].transcribe_status;
    }
  });
  const previousTranscribeStatus = usePrevious(transcribeStatus);

  const fetchTranscriptions = useCallback(
    (
      assetIdValue: string,
      versionIdValue: string,
      transcriptionPropertiesId?: string,
    ) => {
      setIsLoading(true);
      return dispatch(
        getTranscriptionSegments(
          assetIdValue,
          versionIdValue,
          transcriptionPropertiesId,
        ),
      ).finally(() => setIsLoading(false));
    },
    [],
  );

  const fetchData = useCallback(
    async (assetIdValue: string, versionIdValue: string) => {
      setIsLoading(true);
      const allTranscriptionProperties = await dispatch(
        getTranscriptionProperties(assetIdValue, versionIdValue),
      );

      // There can be multiple transcription properties for the same language and we only want the latest for each. This is used to set the source language as the default
      const groupedByLanguage = groupBy(allTranscriptionProperties, 'language');
      const filteredTranscriptionProperties = Object.keys(groupedByLanguage)
        .map((language) => groupedByLanguage[language].at(-1))
        .filter((properties) => properties !== undefined);

      const propertiesId = filteredTranscriptionProperties?.at(0)?.id;

      if (propertiesId) {
        dispatch(
          setSelectedTranscriptionPropertiesId({
            assetId: assetIdValue,
            propertiesId,
          }),
        );
      }
      return fetchTranscriptions(assetIdValue, versionIdValue, propertiesId);
    },
    [fetchTranscriptions],
  );

  useEffect(() => {
    if (assetId && currentVersionId) {
      fetchData(assetId, currentVersionId);
    }
  }, [assetId, currentVersionId, fetchData]);

  useEffect(() => {
    if (
      previousTranscribeStatus &&
      transcribeStatus === 'DONE' &&
      previousTranscribeStatus !== transcribeStatus &&
      currentVersionId &&
      assetId
    ) {
      // We want to force a refetch of the properties here since it might be a re-transcribe
      // in which case there already exists properties but we want to fetch the latest
      fetchData(assetId, currentVersionId);
    }
  }, [
    assetId,
    transcribeStatus,
    previousTranscribeStatus,
    currentVersionId,
    fetchData,
  ]);

  const getLeafValues = (currNode: PrefixTreeNode): Set<string> => {
    const resultIds = new Set<string>();

    const nodeArray = [currNode];
    for (let index = 0; index < nodeArray.length; index += 1) {
      const node = nodeArray[index];
      if (node.values) {
        for (const id of node.values) {
          resultIds.add(id);
        }
      }
      nodeArray.push(...Object.values(node.children));
    }

    return resultIds;
  };

  const fetchIdsFromPrefixTree = (inputString: string): Set<string> => {
    const idsSet = new Set<string>();
    let curPointer: PrefixTreeNode = tree;

    for (let i = 0; i < inputString.length; i += 1) {
      const char = inputString.charAt(i);
      const child = curPointer.children[char];
      if (child) {
        curPointer = child;

        if (i === inputString.length - 1) {
          getLeafValues(curPointer).forEach((id) => {
            idsSet.add(id);
          });
        }
      } else {
        break;
      }
    }

    return idsSet;
  };

  const onSearch = (query = ''): void => {
    const queryWords = query
      .split(' ')
      .map((word) => word.trim().toLowerCase())
      .filter((word) => word.length > 0);

    let idsSet = new Set<string>();

    if (queryWords.length === 1) {
      idsSet = fetchIdsFromPrefixTree(queryWords[0]);
    } else if (queryWords.length > 1) {
      const wordsArray: Set<string>[] = queryWords.map((word) =>
        fetchIdsFromPrefixTree(word),
      );
      wordsArray[0].forEach((wordId) => {
        const localIds = [wordId];

        for (let i = 1; i < wordsArray.length; i += 1) {
          const wordIndex = parseInt(wordId.split('_')[2], 10) + i;
          const expectedWordId = wordId.replace(/\d+$/, wordIndex.toString());

          if (wordsArray[i].has(expectedWordId)) {
            localIds.push(expectedWordId);
          }
        }

        if (localIds.length === queryWords.length) {
          localIds.forEach((id) => {
            idsSet.add(id);
          });
        }
      });
    }

    const ids = [...idsSet];

    setSearchQuery(query);
    setSearchResultIds(ids);
    setSearchResultSegmentsIds(
      ids
        .map((id) => id.split('_')[1])
        .filter((value, index, self) => self.indexOf(value) === index),
    );
    setCurrentMatchIndex(0);
  };

  const onTranscriptionPropertiesChange = (
    propertiesId: string | string[] | undefined | null,
  ): void => {
    if (isString(propertiesId) && assetId) {
      dispatch(setSelectedTranscriptionPropertiesId({ assetId, propertiesId }));
      if (currentVersionId) {
        fetchTranscriptions(assetId, currentVersionId, propertiesId);
      }
      onSearch('');
    }
  };

  const onTranscribeClick = () => {
    if (!asset) {
      return;
    }
    const contextItems = [
      addObjectType(
        {
          ...asset,
        },
        OBJECT_TYPE_ASSETS,
      ),
    ];
    dispatch(openModal({ id: TRANSCRIBE_CONFIRM_MODAL, contextItems }));
  };

  const transcriptionPropertiesOptions = useMemo(
    () =>
      transcriptionProperties.map((properties) => ({
        label:
          languages.find(({ value }) => value === properties.language)?.label ??
          [
            ...AMAZON_TRANSCRIBE_LANGUAGES,
            ...REV_TRANSCRIBE_LANGUAGES,
            ...GOOGLE_TRANSCRIBE_LANGUAGES,
          ].find(({ value }) => value === properties.language)?.label ??
          'Unknown',
        value: properties.id,
      })),
    [transcriptionProperties],
  );

  const emptyContextMenu = useAppSelector((state) =>
    isContextMenuEmpty(getDefinition, state),
  );

  if (isLoading) {
    return <LoadingBar />;
  }

  if (transcriptions.length === 0) {
    return (
      <NoContentPanel
        title="Use AI to transcribe audio to text"
        linkText="Learn more"
        linkHref={TRANSCRIPTIONS}
        actionText="TRANSCRIBE"
        onActionClick={
          userRoles.canTranscribeContent ? onTranscribeClick : undefined
        }
      />
    );
  }

  if (!asset) {
    return null;
  }

  return (
    <div className={styles.root}>
      <div className={styles.header}>
        <SearchInput
          onSearch={onSearch}
          value={searchQuery}
          className={styles.search}
          rightContent={
            <span className={styles.match_counter}>
              {speakerMode && searchQuery
                ? `${totalMatches > 0 ? currentMatchIndex + 1 : 0}/${totalMatches}`
                : ''}
            </span>
          }
        />
        <div className={styles.match_nav_buttons}>
          <TooltipTrigger
            tooltipText="Previous match"
            placement="top"
            component={UADButton}
            componentProps={{
              variant: 'tertiary',
              size: 'm',
              icon: <Icon name="chevron_small_isolated_w" />,
              isDisabled: !speakerMode || totalMatches === 0,
              onPress: handlePrevMatch,
              'aria-label': 'Navigate to previous match',
            }}
          />
          <TooltipTrigger
            tooltipText="Next match"
            placement="top"
            component={UADButton}
            componentProps={{
              variant: 'tertiary',
              size: 'm',
              icon: <Icon name="chevron_small_isolated_e" />,
              isDisabled: !speakerMode || totalMatches === 0,
              onPress: handleNextMatch,
              'aria-label': 'Navigate to next match',
            }}
          />
        </div>
        <SelectField
          className={styles.language_select}
          options={transcriptionPropertiesOptions}
          value={selectedTranscriptionPropertiesId}
          plainMultiValues
          onChange={onTranscriptionPropertiesChange}
          aria={{ label: 'Language' }}
        />

        <div className={styles.header_buttons}>
          <TooltipTrigger
            tooltipText="Speaker mode"
            placement="top"
            component={Button}
            componentProps={{
              variant: VARIANTS.WHITE,
              wideBasis: false,
              noBorder: true,
              iconName: 'conversation',
              aria: { label: 'Speaker mode', pressed: speakerMode },
              onClick: () => dispatch(setSpeakerMode(true)),
              className: classNames({
                [styles.header_button_active]: speakerMode,
              }),
              uiKey: 'transcriptions-speaker-mode-on-button',
            }}
          />
          <TooltipTrigger
            tooltipText="Timecode mode"
            placement="top"
            component={Button}
            componentProps={{
              variant: VARIANTS.WHITE,
              wideBasis: false,
              noBorder: true,
              iconName: 'player_time_format',
              aria: { label: 'Timecode mode', pressed: !speakerMode },
              onClick: () => dispatch(setSpeakerMode(false)),
              className: classNames({
                [styles.header_button_active]: !speakerMode,
              }),
              uiKey: 'transcriptions-speaker-mode-off-button',
            }}
          />
          {!emptyContextMenu ? (
            <TriggerContextMenu
              triggerElement={<MenuTriggerButton />}
              contextMenu={AssetTranscriptionsGearboxContextMenu}
              contextItems={[asset]}
            />
          ) : null}
        </div>
      </div>
      <div className={styles.content}>
        {speakerMode ? (
          <TranscriptionsSpeakerMode
            markedWordIds={searchResultIds}
            currentMatchWordId={currentMatchWordId}
          />
        ) : (
          <TranscriptionsSegmentsMode
            canEdit={userRoles.canWriteTranscriptions}
            canEditSpeakers={asset.type !== 'SUBCLIP'}
            assetProxy={assetProxy}
            speakerLabels={speakerLabels}
            transcriptions={
              searchQuery ? searchResultTranscriptions : transcriptions
            }
          />
        )}
      </div>
      <ModalsManager
        modals={{
          [TRANSCRIPTIONS_EDIT_SPEAKERS]: TranscriptionSpeakersModal,
          [TRANSCRIPTIONS_EDIT_SUBTITLES]: TranscriptionSubtitlesModal,
          [TRANSCRIPTIONS_CHANGE_SEGMENTS_SPEAKER]:
            TranscriptionsConfirmChangeSegmentsSpeakerModal,
          [TRANSCRIPTIONS_CHANGE_WORDS_SPEAKER]:
            TranscriptionsConfirmChangeWordsSpeaker,
        }}
      />
    </div>
  );
}
